import type { AppRouterInstance } from "next/dist/shared/lib/app-router-context.shared-runtime";
import type { ReadonlyURLSearchParams } from "next/navigation";

import { PaginationEllipsis, PaginationItem, PaginationLink } from "@/components/ui/pagination";

interface UsePaginationProps {
  page: number;
  totalPages: number;
  searchParams: ReadonlyURLSearchParams;
  router: AppRouterInstance;
}

export function usePagination({ page, totalPages, searchParams, router }: UsePaginationProps) {
  function navigateToPage(targetPage: number) {
    const next = new URLSearchParams(searchParams.toString());
    if (targetPage > 1) {
      next.set("page", String(targetPage));
    } else {
      next.delete("page");
    }
    router.push("?" + next.toString());
  }

  function renderPageNumbers() {
    const items: React.ReactNode[] = [];
    const maxVisible = 5;
    let start = Math.max(1, page - Math.floor(maxVisible / 2));
    const end = Math.min(totalPages, start + maxVisible - 1);
    start = Math.max(1, end - maxVisible + 1);

    if (start > 1) {
      items.push(
        <PaginationItem key="start">
          <PaginationLink onClick={() => navigateToPage(1)}>1</PaginationLink>
        </PaginationItem>,
      );
      if (start > 2) {
        items.push(
          <PaginationItem key="start-ellipsis">
            <PaginationEllipsis />
          </PaginationItem>,
        );
      }
    }

    for (let i = start; i <= end; i++) {
      items.push(
        <PaginationItem key={i}>
          <PaginationLink isActive={i === page} onClick={() => navigateToPage(i)}>
            {i}
          </PaginationLink>
        </PaginationItem>,
      );
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        items.push(
          <PaginationItem key="end-ellipsis">
            <PaginationEllipsis />
          </PaginationItem>,
        );
      }
      items.push(
        <PaginationItem key="end">
          <PaginationLink onClick={() => navigateToPage(totalPages)}>{totalPages}</PaginationLink>
        </PaginationItem>,
      );
    }

    return items;
  }

  return { navigateToPage, renderPageNumbers };
}
